/**
 * Futures Orders Tab Component
 *
 * Displays futures orders with:
 * - Current position summary
 * - Open short orders (hedge entry)
 * - Close short orders (hedge exit)
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Grid,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import type { StrategyInfo, FuturesOrder, Fill } from '../../../types/strategy';
import { traderTheme as theme } from '../traderDashboardTheme';

interface FuturesOrdersTabProps {
  strategy: StrategyInfo;
}

type OrderFilter = 'all' | 'open' | 'close';

function formatDate(timestamp: number): string {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return date.toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatPrice(price: number | null | undefined): string {
  if (price == null) return '-';
  return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

function formatVolume(value: number | null | undefined): string {
  if (value == null) return '-';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 8,
  });
}

function formatUsdt(value: number | null | undefined): string {
  if (value == null) return '-';
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Calculate volume-weighted average price from fills
 */
function calculateAvgPriceFromFills(fills: Fill[]): number {
  if (!fills || fills.length === 0) return 0;
  let totalVolume = 0;
  let totalValue = 0;
  for (const fill of fills) {
    totalVolume += fill.volume;
    totalValue += fill.price * fill.volume;
  }
  return totalVolume > 0 ? totalValue / totalVolume : 0;
}

/**
 * Get the best available price for display
 * Priority: fills avg price > order avg_price > order price
 */
function getDisplayPrice(order: FuturesOrder): number {
  if (order.fills && order.fills.length > 0) {
    return calculateAvgPriceFromFills(order.fills);
  }
  if (order.avg_price && order.avg_price > 0) {
    return order.avg_price;
  }
  return order.price;
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'Filled':
      return theme.profit;
    case 'PartiallyFilled':
      return theme.warning;
    case 'Pending':
    case 'Open':
    case 'New':
      return theme.accent;
    case 'Cancelled':
    case 'Rejected':
    case 'Error':
      return theme.loss;
    default:
      return theme.text.secondary;
  }
}

interface FuturesOrderRowProps {
  order: FuturesOrder;
  t: (key: string) => string;
}

const FuturesOrderRow: React.FC<FuturesOrderRowProps> = ({ order, t }) => {
  const [expanded, setExpanded] = useState(false);
  const hasFills = order.fills && order.fills.length > 0;

  // Determine if this is an open short or close short
  // In one-way mode (position_side=Both), we only have short positions:
  // Open short: side = Ask (sell) - all Ask orders open shorts
  // Close short: side = Bid (buy) - all Bid orders close shorts (not just reduce_only!)
  const isOpenShort = order.side === 'Ask';
  const isCloseShort = order.side === 'Bid';

  const orderType = isOpenShort
    ? t('traderDashboard.strategyDetail.futuresOrders.openShort')
    : isCloseShort
    ? t('traderDashboard.strategyDetail.futuresOrders.closeShort')
    : order.side;

  const typeColor = isOpenShort ? theme.loss : isCloseShort ? theme.profit : theme.text.secondary;

  // Calculate avg price from fills if available
  const displayPrice = getDisplayPrice(order);
  const notionalValue = displayPrice * order.executed_volume;

  const handleToggle = () => {
    if (hasFills) {
      setExpanded(!expanded);
    }
  };

  return (
    <>
      <Box
        onClick={handleToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 1.5,
          px: 2,
          borderBottom: expanded ? 'none' : `1px solid ${theme.border}`,
          cursor: hasFills ? 'pointer' : 'default',
          '&:hover': {
            backgroundColor: hasFills ? theme.bg.hover : 'transparent',
          },
        }}
      >
        {/* Expand Icon */}
        <Box sx={{ width: '4%' }}>
          {hasFills ? (
            <IconButton size="small" sx={{ p: 0 }}>
              {expanded ? (
                <ExpandLessIcon sx={{ fontSize: 16, color: theme.accent }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 16, color: theme.text.secondary }} />
              )}
            </IconButton>
          ) : (
            <Box sx={{ width: 16 }} />
          )}
        </Box>

        {/* Time */}
        <Box sx={{ width: '11%' }}>
          <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.7rem' }}>
            {formatDate(order.created_at)}
          </Typography>
        </Box>

        {/* Type */}
        <Box sx={{ width: '10%' }}>
          <Chip
            label={orderType}
            size="small"
            sx={{
              backgroundColor: `${typeColor}20`,
              color: typeColor,
              fontSize: '0.65rem',
              height: 18,
              fontWeight: 600,
            }}
          />
        </Box>

        {/* Symbol + Order ID */}
        <Box sx={{ width: '9%' }}>
          <Typography variant="caption" sx={{ color: theme.text.primary, fontSize: '0.75rem' }}>
            {order.pair}
          </Typography>
          <Typography
            variant="caption"
            onClick={() => {
              navigator.clipboard.writeText(order.id);
            }}
            sx={{
              color: theme.text.muted,
              fontSize: '0.6rem',
              display: 'block',
              fontFamily: 'monospace',
              cursor: 'pointer',
              '&:hover': { color: theme.text.primary },
            }}
          >
            {order.id}
          </Typography>
        </Box>

        {/* Avg Price (from fills or order) */}
        <Box sx={{ width: '12%', textAlign: 'right' }}>
          <Typography
            variant="caption"
            sx={{ color: theme.price.futures, fontFamily: 'monospace', fontSize: '0.75rem' }}
          >
            {displayPrice > 0 ? formatPrice(displayPrice) : '-'}
          </Typography>
          {hasFills && (
            <Typography
              variant="caption"
              sx={{ color: theme.text.muted, fontSize: '0.6rem', display: 'block' }}
            >
              ({order.fills!.length} fills)
            </Typography>
          )}
        </Box>

        {/* Volume */}
        <Box sx={{ width: '11%', textAlign: 'right' }}>
          <Typography
            variant="caption"
            sx={{ color: theme.text.primary, fontFamily: 'monospace', fontSize: '0.75rem' }}
          >
            {formatVolume(order.executed_volume)}
          </Typography>
          {order.executed_volume !== order.volume && (
            <Typography
              variant="caption"
              sx={{ color: theme.text.muted, fontFamily: 'monospace', fontSize: '0.6rem', display: 'block' }}
            >
              / {formatVolume(order.volume)}
            </Typography>
          )}
        </Box>

        {/* Status */}
        <Box sx={{ width: '8%', textAlign: 'center' }}>
          <Typography
            variant="caption"
            sx={{ color: getStatusColor(order.status), fontSize: '0.7rem', fontWeight: 500 }}
          >
            {order.status}
          </Typography>
        </Box>

        {/* Commission */}
        <Box sx={{ width: '9%', textAlign: 'right' }}>
          <Typography
            variant="caption"
            sx={{ color: theme.text.secondary, fontFamily: 'monospace', fontSize: '0.7rem' }}
          >
            {order.commission ? formatUsdt(order.commission) : '-'}
          </Typography>
          {order.commission && order.commission > 0 && order.commission_asset && (
            <Typography
              variant="caption"
              sx={{ color: theme.text.muted, fontFamily: 'monospace', fontSize: '0.6rem', display: 'block' }}
            >
              {order.commission.toFixed(6)} {order.commission_asset}
            </Typography>
          )}
        </Box>

        {/* Order Price */}
        <Box sx={{ width: '11%', textAlign: 'right' }}>
          <Typography
            variant="caption"
            sx={{ color: theme.text.secondary, fontFamily: 'monospace', fontSize: '0.75rem' }}
          >
            {formatPrice(order.price)}
          </Typography>
        </Box>

        {/* Notional Value */}
        <Box sx={{ width: '10%', textAlign: 'right' }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.text.primary,
              fontFamily: 'monospace',
              fontWeight: 500,
              fontSize: '0.8rem',
            }}
          >
            {formatUsdt(notionalValue)}
          </Typography>
        </Box>
      </Box>

      {/* Fills Details */}
      {hasFills && (
        <Collapse in={expanded}>
          <Box
            sx={{
              backgroundColor: theme.bg.tertiary || 'rgba(0, 0, 0, 0.2)',
              borderBottom: `1px solid ${theme.border}`,
              py: 1,
              px: 2,
              pl: 6,
            }}
          >
            <Typography variant="caption" sx={{ color: theme.text.secondary, fontWeight: 600, mb: 1, display: 'block' }}>
              {t('traderDashboard.strategyDetail.orders.fills')} ({order.fills!.length})
            </Typography>
            {/* Fills table header */}
            <Box sx={{ display: 'flex', gap: 2, mb: 0.5, borderBottom: `1px solid ${theme.border}`, pb: 0.5 }}>
              <Typography variant="caption" sx={{ width: '25%', color: theme.text.muted, fontSize: '0.6rem' }}>
                {t('traderDashboard.strategyDetail.orders.price')}
              </Typography>
              <Typography variant="caption" sx={{ width: '20%', color: theme.text.muted, fontSize: '0.6rem' }}>
                {t('traderDashboard.strategyDetail.orders.volume')}
              </Typography>
              <Typography variant="caption" sx={{ width: '25%', color: theme.text.muted, fontSize: '0.6rem' }}>
                {t('traderDashboard.strategyDetail.orders.notional')}
              </Typography>
              <Typography variant="caption" sx={{ width: '30%', color: theme.text.muted, fontSize: '0.6rem' }}>
                {t('traderDashboard.strategyDetail.orders.fee')}
              </Typography>
            </Box>
            {/* Fills rows */}
            {order.fills!.map((fill, idx) => {
              const fillNotional = fill.price * fill.volume;
              return (
                <Box key={fill.uid || idx} sx={{ display: 'flex', gap: 2, py: 0.25 }}>
                  <Typography variant="caption" sx={{ width: '25%', color: theme.text.primary, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                    {formatPrice(fill.price)}
                  </Typography>
                  <Typography variant="caption" sx={{ width: '20%', color: theme.text.primary, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                    {formatVolume(fill.volume)}
                  </Typography>
                  <Typography variant="caption" sx={{ width: '25%', color: theme.text.primary, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                    {formatUsdt(fillNotional)}
                  </Typography>
                  <Typography variant="caption" sx={{ width: '30%', color: theme.text.secondary, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                    {fill.fee != null ? `${fill.fee.toFixed(6)} ${fill.fee_currency || ''}` : '-'}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Collapse>
      )}
    </>
  );
};

export const FuturesOrdersTab: React.FC<FuturesOrdersTabProps> = ({ strategy }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<OrderFilter>('all');

  const ctx = strategy.execution_context;

  const orders = useMemo(() => {
    const allOrders = Object.values(ctx.futures_orders) as FuturesOrder[];
    return allOrders.sort((a, b) => b.created_at - a.created_at);
  }, [ctx.futures_orders]);

  const filteredOrders = useMemo(() => {
    if (filter === 'all') return orders;
    // In one-way mode (position_side=Both), Ask = Open Short, Bid = Close Short
    // reduce_only flag is optional - any Bid order closes a short position
    if (filter === 'open') return orders.filter((o) => o.side === 'Ask');
    return orders.filter((o) => o.side === 'Bid');
  }, [orders, filter]);

  // Calculate totals
  // In one-way mode (position_side=Both): Ask = Open Short, Bid = Close Short
  // reduce_only flag is optional - any Bid order closes a short position
  const totals = useMemo(() => {
    const openOrders = orders.filter((o) => o.side === 'Ask' && o.executed_volume > 0);
    const closeOrders = orders.filter((o) => o.side === 'Bid' && o.executed_volume > 0);

    const totalOpenNotional = openOrders.reduce((sum, o) => {
      const price = o.avg_price || o.price;
      return sum + price * o.executed_volume;
    }, 0);

    const totalCloseNotional = closeOrders.reduce((sum, o) => {
      const price = o.avg_price || o.price;
      return sum + price * o.executed_volume;
    }, 0);

    const totalOpenVolume = openOrders.reduce((sum, o) => sum + o.executed_volume, 0);
    const totalCloseVolume = closeOrders.reduce((sum, o) => sum + o.executed_volume, 0);

    const totalCommission = orders.reduce((sum, o) => sum + (o.commission || 0), 0);

    // Realized P&L from closed positions (profit when we close lower than we opened)
    const realizedPnl = totalOpenNotional - totalCloseNotional;

    return {
      openCount: openOrders.length,
      closeCount: closeOrders.length,
      totalOpenNotional,
      totalCloseNotional,
      totalOpenVolume,
      totalCloseVolume,
      totalCommission,
      realizedPnl,
      remainingVolume: totalOpenVolume - totalCloseVolume,
    };
  }, [orders]);

  const handleFilterChange = (_event: React.MouseEvent<HTMLElement>, newFilter: OrderFilter | null) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  return (
    <Box>
      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Paper
          sx={{
            p: 1.5,
            flex: 1,
            backgroundColor: theme.bg.secondary,
            border: `1px solid ${theme.border}`,
          }}
        >
          <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.7rem' }}>
            {t('traderDashboard.strategyDetail.futuresOrders.openShortTotal')}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.text.primary, fontFamily: 'monospace', fontWeight: 600 }}
          >
            {formatUsdt(totals.totalOpenNotional)}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.text.muted, fontSize: '0.65rem' }}>
            {totals.openCount} {t('traderDashboard.strategyDetail.orders.orders')} • {formatVolume(totals.totalOpenVolume)}
          </Typography>
        </Paper>
        <Paper
          sx={{
            p: 1.5,
            flex: 1,
            backgroundColor: theme.bg.secondary,
            border: `1px solid ${theme.border}`,
          }}
        >
          <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.7rem' }}>
            {t('traderDashboard.strategyDetail.futuresOrders.closeShortTotal')}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.text.primary, fontFamily: 'monospace', fontWeight: 600 }}
          >
            {formatUsdt(totals.totalCloseNotional)}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.text.muted, fontSize: '0.65rem' }}>
            {totals.closeCount} {t('traderDashboard.strategyDetail.orders.orders')} • {formatVolume(totals.totalCloseVolume)}
          </Typography>
        </Paper>
        <Paper
          sx={{
            p: 1.5,
            flex: 1,
            backgroundColor: theme.bg.secondary,
            border: `1px solid ${Math.abs(totals.remainingVolume) < 0.00000001 ? theme.profit : theme.warning}40`,
          }}
        >
          <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.7rem' }}>
            {t('traderDashboard.strategyDetail.futuresOrders.remainingPosition')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: Math.abs(totals.remainingVolume) < 0.00000001 ? theme.profit : theme.warning,
              fontFamily: 'monospace',
              fontWeight: 600,
            }}
          >
            {formatVolume(totals.remainingVolume)} {ctx.opportunity?.base || ''}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.text.muted, fontSize: '0.65rem' }}>
            {Math.abs(totals.remainingVolume) < 0.00000001
              ? t('traderDashboard.strategyDetail.futuresOrders.positionClosed')
              : t('traderDashboard.strategyDetail.futuresOrders.positionOpen')}
          </Typography>
        </Paper>
        <Paper
          sx={{
            p: 1.5,
            flex: 1,
            backgroundColor: theme.bg.secondary,
            border: `1px solid ${totals.realizedPnl >= 0 ? theme.profit : theme.loss}40`,
          }}
        >
          <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.7rem' }}>
            {t('traderDashboard.strategyDetail.futuresOrders.realizedPnl')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: totals.realizedPnl >= 0 ? theme.profit : theme.loss,
              fontFamily: 'monospace',
              fontWeight: 600,
            }}
          >
            {totals.realizedPnl >= 0 ? '+' : ''}{formatUsdt(totals.realizedPnl)}
          </Typography>
        </Paper>
      </Box>

      {/* Filter */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ color: theme.text.primary, fontWeight: 600 }}>
          {t('traderDashboard.strategyDetail.futuresOrders.orderHistory')} ({filteredOrders.length})
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
            <Typography variant="caption" sx={{ color: theme.loss, fontFamily: 'monospace', fontSize: '0.75rem' }}>
              오픈: {formatVolume(totals.totalOpenVolume)}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.profit, fontFamily: 'monospace', fontSize: '0.75rem' }}>
              청산: {formatVolume(totals.totalCloseVolume)}
            </Typography>
          </Box>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={handleFilterChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                color: theme.text.secondary,
                borderColor: theme.border,
                fontSize: '0.75rem',
                py: 0.5,
                px: 1.5,
                '&.Mui-selected': {
                  backgroundColor: `${theme.accent}20`,
                  color: theme.accent,
                },
              },
            }}
          >
            <ToggleButton value="all">{t('traderDashboard.strategyDetail.orders.all')}</ToggleButton>
            <ToggleButton value="open">{t('traderDashboard.strategyDetail.futuresOrders.openShort')}</ToggleButton>
            <ToggleButton value="close">{t('traderDashboard.strategyDetail.futuresOrders.closeShort')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Orders Table */}
      <Paper
        sx={{
          backgroundColor: theme.bg.secondary,
          border: `1px solid ${theme.border}`,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1,
            px: 2,
            backgroundColor: theme.bg.primary,
            borderBottom: `2px solid ${theme.border}`,
          }}
        >
          <Box sx={{ width: '4%' }} /> {/* Expand icon column */}
          <Typography variant="caption" sx={{ width: '11%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem' }}>
            {t('traderDashboard.strategyDetail.orders.time')}
          </Typography>
          <Typography variant="caption" sx={{ width: '10%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem' }}>
            {t('traderDashboard.strategyDetail.orders.type')}
          </Typography>
          <Typography variant="caption" sx={{ width: '9%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem' }}>
            {t('traderDashboard.strategyDetail.orders.symbol')}
          </Typography>
          <Typography variant="caption" sx={{ width: '12%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
            {t('traderDashboard.strategyDetail.orders.avgPrice')}
          </Typography>
          <Typography variant="caption" sx={{ width: '11%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
            {t('traderDashboard.strategyDetail.orders.volume')}
          </Typography>
          <Typography variant="caption" sx={{ width: '8%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'center' }}>
            {t('traderDashboard.strategyDetail.orders.status')}
          </Typography>
          <Typography variant="caption" sx={{ width: '9%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
            {t('traderDashboard.strategyDetail.orders.fee')}
          </Typography>
          <Typography variant="caption" sx={{ width: '11%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
            {t('traderDashboard.strategyDetail.orders.orderPrice')}
          </Typography>
          <Typography variant="caption" sx={{ width: '10%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
            {t('traderDashboard.strategyDetail.orders.notional')}
          </Typography>
        </Box>

        {/* Rows */}
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {filteredOrders.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: theme.text.secondary }}>
                {t('traderDashboard.strategyDetail.orders.noOrders')}
              </Typography>
            </Box>
          ) : (
            filteredOrders.map((order) => (
              <FuturesOrderRow key={order.id} order={order} t={t} />
            ))
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default FuturesOrdersTab;
